import {Plural, Trans} from '@lingui/macro';
import _ from 'lodash';
import React, {FC} from 'react';
import {createSelector} from 'reselect';
import styled from 'styled-components';

import {AsyncHandler, Handler} from '../../../../libs/shared/helpers/src/react/reactHelpers';
import {ButtonVariantsEnum} from '../../../../libs/shared/ui/src/components/buttons/buttonConstants';
import {OkCancelDialog} from '../../../components/common/dialogs/okCancelDialog';
import {useAppSelector} from '../../../helpers/reduxHelpers';
import {SignatureModel} from '../../../models/api/signatureModel';
import {getTeamsByNamespace} from '../../../store/data/teams/teamsSelectors';

/*
 * Props.
 */

interface SignaturesDeleteDialogProps {
  signatures: ReadonlyArray<SignatureModel>;
  onClose: Handler;
  onDelete: AsyncHandler;
}

/*
 * Styles.
 */

const StyledOkCancelDialog = styled(OkCancelDialog)`
  margin: 0 16px;
`;

/*
 * Component.
 */

export const SignaturesDeleteDialog: FC<SignaturesDeleteDialogProps> = (props) => {
  const {onClose, onDelete} = props;
  const signatures = _(props.signatures).compact().value();
  const getTeamNameByNameSpace = makeGetTeamNameByNameSpace(signatures[0]?.namespace);
  const teamNameOrPersonal = useAppSelector(getTeamNameByNameSpace) ?? 'Personal';

  if (signatures.length === 0) {
    return null;
  }

  return (
    <StyledOkCancelDialog
      title={renderTitle(signatures)}
      okButtonLabel={
        <Trans id="delete-signatures-confirmation-dialog-delete-permanently">Delete permanently</Trans>
      }
      okButtonVariant={ButtonVariantsEnum.DANGER}
      onRequestClose={onClose}
      onOkClick={onDelete}
    >
      <Plural
        id="delete-signatures-confirmation-dialog-description"
        value={signatures.length}
        one={`This signature will be permanently deleted from ${teamNameOrPersonal}. This action cannot be undone.`}
        other={`These signatures will be permanently deleted from ${teamNameOrPersonal}. This action cannot be undone.`}
      />
    </StyledOkCancelDialog>
  );
};

/*
 * Helpers.
 */

function renderTitle(signatures: ReadonlyArray<SignatureModel | undefined>) {
  return (
    <Plural
      id="delete-signatures-confirmation-dialog-title"
      value={signatures.length}
      one="Delete this signature"
      other="Delete # signatures"
    />
  );
}

const makeGetTeamNameByNameSpace = (namespace: string) =>
  createSelector([getTeamsByNamespace], (teamsByNamespace) => teamsByNamespace[namespace]?.name);
